'use client';

import { captureAnalyticsClientEvent } from '@/analytics/analytics-client';
import { useWorkspaceUploadDrop } from '@/cms/components/blocks/workspace/workspace-1/shell/use-workspace-upload-drop';
import { WorkspaceMobileDrawer } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-mobile-drawer';
import {
  WorkspaceUploadThumbnailCard,
  WorkspaceUploadTileButton,
} from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-upload-panel';
import { useWorkspaceIsMobileViewport } from '@/cms/components/blocks/workspace/workspace-1/shell/workspace-viewport';
import { useContactAttachments } from '@/components/contact/hooks/use-contact-attachments';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/overlay/dialog';
import {
  CONTACT_ATTACHMENT_ACCEPTED_TYPES,
  CONTACT_ATTACHMENT_MAX_COUNT,
} from '@/lib/support/contact';
import {
  WORKSPACE_FEEDBACK_MAX_LENGTH,
  workspaceFeedbackSchema,
} from '@/lib/support/workspace-feedback-contract';
import { toast } from '@/lib/ui/toast';
import { cn } from '@/lib/ui/utils';
import { useTranslations } from 'next-intl';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { z } from 'zod';
import styles from './cms-workspace-sidebar-tools.module.css';

export function WorkspaceFeedbackDialog({
  open,
  onOpenChange,
  user,
  disabled,
  locale,
  currentPath,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { name?: string | null; email?: string | null } | null;
  disabled: boolean;
  locale: string;
  currentPath: string;
}) {
  const t = useTranslations('Common.workspaceSidebar');
  const isMobile = useWorkspaceIsMobileViewport();
  const drawerId = useId();
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const messageInput = useRef<HTMLTextAreaElement>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (open && isMobile) {
      captureAnalyticsClientEvent('workspace_feedback_viewed', {});
    }
  }, [open, isMobile]);
  const attachments = useContactAttachments({ t });
  const attachmentLimitReached =
    attachments.attachments.length >= CONTACT_ATTACHMENT_MAX_COUNT;
  const attachmentDrop = useWorkspaceUploadDrop(
    attachments.addAttachments,
    attachmentLimitReached
  );
  const canSend =
    !disabled &&
    !sending &&
    !attachments.hasPendingUploads &&
    Boolean(message.trim());

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSend || sendingRef.current) return;
    const parsed = workspaceFeedbackSchema.safeParse({
      name: name.trim(),
      email: email.trim(),
      message,
      locale,
      pagePath: currentPath,
      attachments: attachments.attachments.map(({ key, url }) => ({
        key,
        url,
      })),
    });
    if (!parsed.success) {
      setError(t('invalidFeedback'));
      return;
    }
    sendingRef.current = true;
    setSending(true);
    setError('');
    try {
      const response = await fetch('/api/workspace/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed.data),
      });
      const result = z
        .object({
          success: z.boolean().optional(),
          error: z.string().optional(),
        })
        .parse(await response.json());
      if (!response.ok || result.success !== true) {
        setError(
          t(
            response.status === 429
              ? 'rateLimited'
              : result.error === 'invalid_attachment'
                ? 'invalidAttachment'
                : 'sendFailed'
          )
        );
        captureAnalyticsClientEvent('workspace_feedback_failed', {
          error_code: String(response.status),
        });
        return;
      }
      captureAnalyticsClientEvent('workspace_feedback_submitted', {
        attachment_count: attachments.attachments.length,
      });
      setMessage('');
      attachments.resetAttachments();
      onOpenChange(false);
      toast.success(t('sent'));
    } catch {
      setError(t('sendFailed'));
      captureAnalyticsClientEvent('workspace_feedback_failed', {
        error_code: 'network',
      });
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const changeOpen = (value: boolean) => {
    if (!sendingRef.current) onOpenChange(value);
  };
  const form = (
    <form onSubmit={submit} className={styles.form}>
      <fieldset disabled={sending || disabled} className={styles.fields}>
        <label className={styles.field}>
          <span>{t('messageLabel')} *</span>
          <textarea
            ref={messageInput}
            required
            maxLength={WORKSPACE_FEEDBACK_MAX_LENGTH}
            rows={5}
            value={message}
            className={styles.input}
            placeholder={t('messagePlaceholder')}
            onChange={(event) => setMessage(event.target.value)}
            onPaste={attachments.handlePaste}
          />
        </label>
        <div className={styles.contactFields}>
          <label className={styles.field}>
            <span>
              {t('nameLabel')}{' '}
              <span className={styles.optional}>{t('optional')}</span>
            </span>
            <input
              className={styles.input}
              autoComplete="name"
              name="name"
              maxLength={100}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span>
              {t('emailLabel')}{' '}
              <span className={styles.optional}>{t('optional')}</span>
            </span>
            <input
              className={styles.input}
              type="email"
              autoComplete="email"
              name="email"
              maxLength={254}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
        </div>
        <input
          className="cms-block-workspace-workspace-1-workspace-form-controls__hiddenInput"
          type="file"
          multiple
          accept={CONTACT_ATTACHMENT_ACCEPTED_TYPES.join(',')}
          aria-label={t('addImages')}
          ref={attachments.attachmentInputRef}
          onChange={attachments.handleAttachmentInputChange}
        />
        <div
          className={cn(
            'cms-block-workspace-workspace-1-workspace-form-controls__uploadStrip',
            attachments.attachments.length === 0 &&
              'cms-block-workspace-workspace-1-workspace-form-controls__uploadStripEmpty'
          )}
          {...attachmentDrop}
        >
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__uploadStripInner">
            {!attachmentLimitReached ? (
              <WorkspaceUploadTileButton
                label={t('addImages')}
                onClick={() => attachments.attachmentInputRef.current?.click()}
              />
            ) : null}
            {attachments.attachments.map((item) => (
              <WorkspaceUploadThumbnailCard
                key={item.id}
                previewUrl={item.previewUrl}
                alt={item.name}
                uploadStatus={item.uploadStatus}
                uploadingLabel={t('attachmentUploading')}
                failedLabel={t('attachmentUploadFailed')}
                removeLabel={`${t('removeImage')}: ${item.name}`}
                onRemove={() => attachments.handleRemoveAttachment(item.id)}
              />
            ))}
          </div>
        </div>
        <p className={styles.optional}>{attachments.attachmentsHint}</p>
      </fieldset>
      {error ? (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      ) : null}
      <button type="submit" className={styles.submit} disabled={!canSend}>
        {t(sending ? 'sending' : 'submit')}
      </button>
    </form>
  );

  if (isMobile) {
    return (
      <WorkspaceMobileDrawer
        id={drawerId}
        open={open}
        onOpenChange={changeOpen}
        description={t('feedbackDescription')}
        title={
          <div className={styles.dialogHeading}>
            <span>{t('feedbackTitle')}</span>
            <button
              type="button"
              className={styles.closeButton}
              aria-label={t('close')}
              disabled={sending}
              onClick={() => changeOpen(false)}
            >
              {renderAppIcon({ name: 'X', className: styles.icon })}
            </button>
          </div>
        }
      >
        <div dir={locale === 'ar' ? 'rtl' : 'ltr'}>{form}</div>
      </WorkspaceMobileDrawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent
        className={`cms-theme cms-theme-workspace ${styles.feedbackDialog}`}
        showCloseButton={false}
        dir={locale === 'ar' ? 'rtl' : 'ltr'}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          messageInput.current?.focus();
          captureAnalyticsClientEvent('workspace_feedback_viewed', {});
        }}
      >
        <div className={styles.dialogHeading}>
          <DialogTitle>{t('feedbackTitle')}</DialogTitle>
          <DialogClose
            className={styles.closeButton}
            aria-label={t('close')}
            disabled={sending}
          >
            {renderAppIcon({ name: 'X', className: styles.icon })}
          </DialogClose>
        </div>
        <DialogDescription>{t('feedbackDescription')}</DialogDescription>
        {form}
      </DialogContent>
    </Dialog>
  );
}
